import { useDialogContext } from '@communities/content-management/components/CreatePolicyDialog/components/DialogProvider';
import type { DialogUsernameLabels } from '@communities/content-management/components/CreatePolicyDialog/types';
import { LabelSelector, type SeverityLabelOption, type Label, type SeverityConfig, getSeverityLabel } from '@core/components/LabelSelector';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useMemo } from 'react';

import { stepFormStyles, inputGroupStyles } from './Steps.css';

/**
 * UsernameLabelsStep component props
 */
export interface UsernameLabelsStepProps {
  standardLabels: StandardLabel[];
  labelsLoading: boolean;
}

/**
 * UsernameLabelsStep component
 * Handles username content labeling configuration.
 * Username labels are severity-based, functioning like text labels.
 */
export function UsernameLabelsStep({ standardLabels, labelsLoading }: UsernameLabelsStepProps) {
  const { state, actions } = useDialogContext();
  const { usernameLabels } = state.formData;

  // Filter standard labels for username content
  const usernameLabelOptions: SeverityLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Username)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [standardLabels]
  );

  // Configuration for severity-based labels
  const severityConfig: SeverityConfig = useMemo(
    () => ({
      mode: 'severity' as const,
      min: 1,
      defaultValue: 2,
      defaultMax: 3,
      showDescription: true,
      getValueLabel: getSeverityLabel,
    }),
    []
  );

  const handleUsernameLabelsChange = (labels: Label[]) => {
    const updatedUsernameLabels: DialogUsernameLabels = {
      usernameLabels: labels,
    };

    actions.updateFormData({
      usernameLabels: updatedUsernameLabels,
    });
  };

  return (
    <div className={stepFormStyles}>
      <div className={inputGroupStyles}>
        {usernameLabelOptions.length > 0 ? (
          <LabelSelector
            enableBulkSelection
            availableLabels={usernameLabelOptions}
            config={severityConfig}
            emptyStateMessage="No username labels selected. Add labels to define username content criteria."
            loading={labelsLoading}
            selectedLabels={usernameLabels.usernameLabels}
            title="Username Content Labels"
            onChange={handleUsernameLabelsChange}
          />
        ) : (
          <div aria-live="polite" role="status">
            {labelsLoading ? <p>Loading username labels...</p> : <p>No username labels available. Please configure labels in the system settings.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
